/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import ListingCategories from "@eisinc/App/Components/ListingCategories/ListingCategories";
import PDFViewer from "@eisinc/App/Components/PDFViewer/PDFViewer";
import XngCarousel, { SwiperStyle } from "@eisinc/App/Components/XngCarousel";
// import ProductCarouselComponent from "@eisinc/App/Components/ProductCarouselComponent/ProductCarouselComponent";
import loadKnowledgeCenterDetails from "@eisinc/App/Handlers/LoadKnowledgeCenterDetails";
import { sharedCarouselStyles } from "@eisinc/App/SharedStyles/ProductCarouselStyles";
import { CustomAppState, DocumentModel } from "@eisinc/App/Store/Reducers";
import { getDocumentsState } from "@eisinc/App/Store/Selectors/DocumentsSelector";
import Button from "@eisinc/MobiusOverrides/Button";
import ProductCarouselProduct, {
    ProductCarouselProductStyles,
} from "@eisinc/Overrides/Components/ProductCarouselProduct";
import { CarouselCardType } from "@eisinc/XDL/types";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getBrandById } from "@insite/client-framework/Services/BrandService";
import setBreadcrumbs from "@insite/client-framework/Store/Components/Breadcrumbs/Handlers/SetBreadcrumbs";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { getCategoriesDataView } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link from "@insite/mobius/Link/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { Fragment, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    view = "view",
    showImage = "showImage",
    showTitle = "showTitle",
    showBrandName = "showBrandName",
    showPartNumbers = "showPartNumbers",
    showPrice = "showPrice",
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
    showImageBadges = "showImageBadges",
    maximumNumberOfImageBadges = "maximumNumberOfImageBadges",
    showTextBadges = "showTextBadges",
    maximumNumberOfTextBadges = "maximumNumberOfTextBadges",
    showAvailability = "showAvailability",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.view]: CarouselCardType;
        [fields.showImage]: boolean;
        [fields.showTitle]: boolean;
        [fields.showBrandName]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
        [fields.showImageBadges]: boolean;
        [fields.maximumNumberOfImageBadges]: number;
        [fields.showTextBadges]: boolean;
        [fields.maximumNumberOfTextBadges]: number;
        [fields.showAvailability]: boolean;
    };
}

const mapStateToProps = (state: CustomAppState) => ({
    location: getLocation(state),
    url: state?.context?.website?.uri?.split("/")?.splice(0, 3)?.join("/"),
    categoriesDataView: getCategoriesDataView(state),
    contactUsLink: getPageLinkByPageType(state, "contact-us")?.url || "/contact-us",
    documents: getDocumentsState(state)?.documents,
    isTabletScreen: state.xCommon.isTabletScreen,
});

const mapDispatchToProps = {
    loadKnowledgeCenterDetails,
    loadCarouselProducts,
    loadProductInfoList,
    setBreadcrumbs,
    loadCategories,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory & OwnProps;

export interface KnowledgeCenterStyles {
    wrapper?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    tagsDiv?: InjectableCss;
    typeWrapper?: InjectableCss;
    relatedProductsWrapper?: InjectableCss;
    carouselStyles?: SwiperStyle;
    carouselProductCondensedStyles?: ProductCarouselProductStyles;
    miniCardStyles?: ProductCarouselProductStyles;
    carouselProductStyles?: ProductCarouselProductStyles;
}

export const styles: KnowledgeCenterStyles = {
    typeWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            margin-top: 12px;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        color: "primary.main",
        css: css`
            margin: 0 auto;
        `,
    },
    tagsDiv: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            padding: 4px;
        `,
    },
    carouselProductCondensedStyles: {
        ...sharedCarouselStyles.carouselProductCondensedStyles,
    },
    miniCardStyles: {
        ...sharedCarouselStyles.miniCardStyles,
    },
    carouselProductStyles: {
        ...sharedCarouselStyles.carouselProductStyles,
    },
    carouselStyles: {
        container: {
            css: css`
                .swiper-wrapper {
                    justify-content: center;
                }
            `,
        },
    },
    relatedProductsWrapper: {
        css: css`
            margin-top: 20px;
        `,
    },
};

const KnowledgeCenter: React.FC<Props> = ({
    loadKnowledgeCenterDetails,
    loadProductInfoList,
    setBreadcrumbs,
    location,
    url,
    categoriesDataView,
    loadCategories,
    contactUsLink,
    history,
    documents,
    fields,
    isTabletScreen,
}) => {
    const [dataLoaded, setDataLoaded] = useState(false);
    const [loadedDocument, setLoadedDocument] = useState({} as any);
    const [products, setProducts] = useState<ProductModel[]>([]);
    const [brand, setBrand] = useState({} as any);
    const [embedDocument, setEmbedDocument] = useState(false);

    const breadCrumbLinks = [
        {
            children: "Home",
            href: "/",
        },
        {
            children: "Knowledge Center",
            href: "/Knowledge-Center",
        },
    ];

    const loadData = () => {
        const urlParams = location.search.split("?id=")[1];
        const params: any = {
            id: urlParams,
        };
        if (params.id !== undefined && params.id !== "") {
            const canonical = (document as any)?.querySelector('link[rel="canonical"]');
            const url = canonical?.getAttribute("href");
            canonical?.setAttribute("href", url ? `${url}?id=${params.id}` : "");

            const storeDocument = documents?.filter((doc: DocumentModel) => doc.id === params.id);

            if (storeDocument?.length) {
                const links = [...breadCrumbLinks, { children: storeDocument[0].fileName, href: "" }];
                setDataLoaded(true);
                setBreadcrumbs({ links });
                setLoadedDocument(storeDocument[0]);
                setEmbedDocument(storeDocument[0].embedDocument);
                loadBrand({ brandId: storeDocument[0].brandsId[0] });
                const productIds = storeDocument[0].productsId;
                loadProductInfoList({
                    id: `product-carousel-knowledge-center`,
                    getProductCollectionParameter: { productIds },
                    onComplete(result: any) {
                        if (result?.products) {
                            setProducts(result.products);
                        }
                    },
                });
            } else {
                loadKnowledgeCenterDetails({
                    params,
                    onComplete: (response: any) => {
                        const apiResult = response.apiResult;
                        if (apiResult) {
                            const links = [...breadCrumbLinks, { children: apiResult.document.fileName, href: "" }];
                            setDataLoaded(true);
                            setBreadcrumbs({ links });
                            setLoadedDocument(apiResult.document);
                            setEmbedDocument(apiResult.document.embedDocument);
                            loadBrand({ brandId: apiResult.document.brandsId[0] });
                            const productIds = apiResult.document.productsId;
                            loadProductInfoList({
                                id: `product-carousel-knowledge-center`,
                                getProductCollectionParameter: { productIds },
                                onComplete(result: any) {
                                    if (result?.products) {
                                        setProducts(result.products);
                                    }
                                },
                            });
                        }
                    },
                });
            }
        }
        if (!categoriesDataView.isLoading && !categoriesDataView.value) {
            loadCategories();
        }
    };

    React.useEffect(() => {
        loadData();
    }, [location]);

    const loadBrand = async (id: any) => {
        const apiBrand = await getBrandById(id);
        setBrand(apiBrand);
    };

    const goToContactUs = () => {
        history.push(contactUsLink);
    };

    if (!dataLoaded) {
        return (
            <StyledWrapper
                css={css`
                    height: 300px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                `}
            >
                <LoadingSpinner color="primary.main" />
            </StyledWrapper>
        );
    }

    const jsonLd: any = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@context": "https://schema.org",
                "@type": "DigitalDocument",
                name: loadedDocument?.fileName || loadedDocument?.fileDescription,
                description: `${loadedDocument?.productCategory}, ${loadedDocument.fileDescription} ${loadedDocument.documentType}`,
                url: url + location.pathname + location.search,
            },
        ],
    };

    if (!categoriesDataView.value || !loadedDocument) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    const categories = categoriesDataView?.value.filter(
        category => loadedDocument?.categoriesId?.indexOf(category.id) > -1,
    );

    const carouselCardStyles =
        fields.view === "condensed"
            ? styles.carouselProductCondensedStyles
            : fields.view === "miniCard" || (isTabletScreen && fields.view === "grid")
            ? styles.miniCardStyles
            : styles.carouselProductStyles;

    return (
        <StyledWrapper>
            <Typography variant="h1">{loadedDocument?.fileName}</Typography>
            <Typography variant="h3">{loadedDocument?.fileDescription}</Typography>
            <Typography
                as="div"
                css={css`
                    display: flex;
                `}
            >
                <Typography
                    as="div"
                    css={css`
                        padding: 4px;
                        padding-left: 0;
                    `}
                >
                    {translate("Category: ")}
                </Typography>
                <Typography as="div" {...styles.tagsDiv}>
                    <ListingCategories
                        items={categories}
                        viewMoreButtonAsText={true}
                        categoriesNumber={5}
                        view="inline"
                    />
                </Typography>
            </Typography>
            {Object.keys(brand).length !== 0 && (
                <Typography as="p">
                    {translate("Brand: ")} <Link href={brand.detailPagePath}>{brand.name}</Link>
                </Typography>
            )}
            <StyledWrapper {...styles.typeWrapper}>
                {loadedDocument.documentType && (
                    <Typography as="p">
                        {translate("Type: ")} {loadedDocument?.documentType}
                    </Typography>
                )}

                <Button onClick={goToContactUs} variant="secondary">
                    {translate("Request Info")}
                </Button>
            </StyledWrapper>
            <PDFViewer
                filePath={loadedDocument?.fileUrl}
                fileName={loadedDocument?.fileName}
                dataTestSelector={`knowledge_center_${loadedDocument?.fileName}_${loadedDocument?.filePath}`}
                asLink={!embedDocument}
                newTab={true}
            />
            {products && products.length > 0 && (
                <StyledWrapper {...styles.relatedProductsWrapper}>
                    <Typography variant="h2">{translate("Related Products")}</Typography>
                    <XngCarousel
                        id="product-carousel-knowledge-center"
                        navigation={true}
                        spaceBetween={fields.view === "condensed" ? 24 : 22}
                        scrollbar={true}
                        shadowWidth="64px"
                        showArrowsOnMobile={false}
                        extendedStyles={styles.carouselStyles}
                    >
                        {products.map((product, index) => (
                            <Fragment key={product.id}>
                                <ProductCarouselProduct
                                    carouselId="product-carousel-knowledge-center"
                                    product={product}
                                    showImage={fields.showImage}
                                    showBrand={fields.showBrandName && fields.view !== "condensed"}
                                    showTitle={fields.showTitle}
                                    showPartNumbers={fields.showPartNumbers && fields.view !== "condensed"}
                                    showPrice={fields.showPrice}
                                    showAddToCart={fields.showAddToCart}
                                    showAddToList={fields.showAddToList && fields.view !== "condensed"}
                                    showImageBadges={fields.showImageBadges}
                                    maximumNumberOfImageBadges={fields.maximumNumberOfImageBadges}
                                    showTextBadges={fields.showTextBadges}
                                    maximumNumberOfTextBadges={fields.maximumNumberOfTextBadges}
                                    view={isTabletScreen && fields.view === "grid" ? "miniCard" : fields.view}
                                    showAvailability={fields.showAvailability}
                                    extendedStyles={carouselCardStyles}
                                />
                            </Fragment>
                        ))}
                    </XngCarousel>
                </StyledWrapper>
            )}
            {/* eslint-disable-next-line react/no-danger */}
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
        </StyledWrapper>
    );
};
export const definition: WidgetDefinition = {
    group: "Basic",
    fieldDefinitions: [
        {
            name: fields.view,
            displayName: "Card Type",
            editorTemplate: "RadioButtonsField",
            defaultValue: "grid",
            horizontal: true,
            fieldType: "General",
            options: [
                {
                    displayName: "Grid",
                    value: "grid",
                },
                {
                    displayName: "Condensed",
                    value: "miniCard",
                },
                {
                    displayName: "Thumbnail",
                    value: "condensed",
                },
            ],
        },
        {
            name: fields.showImage,
            displayName: "Image",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            isEnabled: () => false,
            fieldType: "Contextual",
        },
        {
            name: fields.showTitle,
            displayName: "Title",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            isEnabled: () => false,
            fieldType: "Contextual",
        },
        {
            name: fields.showBrandName,
            displayName: "Brand Name",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "Contextual",
            isVisible: (item: any) => item.fields[fields.view] !== "condensed",
        },
        {
            name: fields.showPartNumbers,
            displayName: "Part Numbers",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "Contextual",
            isVisible: (item: any) => item.fields[fields.view] !== "condensed",
        },
        {
            name: fields.showPrice,
            displayName: "Price",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "Contextual",
        },
        {
            name: fields.showAddToCart,
            displayName: "Add to Cart",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "Contextual",
        },
        {
            name: fields.showAddToList,
            displayName: "Add to List",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "Contextual",
            isVisible: (item: any) => item.fields[fields.view] !== "condensed",
        },
        {
            name: fields.showAvailability,
            displayName: "Availability",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "Contextual",
        },

        {
            name: fields.showImageBadges,
            displayName: "Image Badges",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "Contextual",
        },
        {
            name: fields.maximumNumberOfImageBadges,
            editorTemplate: "DropDownField",
            isVisible: (widget: any) => widget.fields.showImageBadges,
            defaultValue: "4",
            options: [
                { displayName: "1", value: "1" },
                { displayName: "2", value: "2" },
                { displayName: "3", value: "3" },
                { displayName: "4", value: "4" },
                { displayName: "5", value: "5" },
                { displayName: "6", value: "6" },
                { displayName: "7", value: "7" },
                { displayName: "8", value: "8" },
            ],
            fieldType: "Contextual",
        },
        {
            name: fields.showTextBadges,
            displayName: "Text Badges",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "Contextual",
        },
        {
            name: fields.maximumNumberOfTextBadges,
            editorTemplate: "DropDownField",
            isVisible: (widget: any) => widget.fields.showTextBadges,
            defaultValue: "4",
            options: [
                { displayName: "1", value: "1" },
                { displayName: "2", value: "2" },
                { displayName: "3", value: "3" },
                { displayName: "4", value: "4" },
                { displayName: "5", value: "5" },
                { displayName: "6", value: "6" },
                { displayName: "7", value: "7" },
                { displayName: "8", value: "8" },
            ],
            fieldType: "Contextual",
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(KnowledgeCenter)),
    definition,
};

export default widgetModule;
